import React, { useCallback, useEffect, useState } from "react";
import axios from "axios";
import { Dialog, DialogActions, DialogContent, DialogTitle, MenuItem, Select, TablePagination } from "@material-ui/core";
import { Link } from "react-router-dom";

import TablePaginationActions from "../../component/table/TablePagination";
import { Toast } from "../../util/Toast";
import { FEATURES, useAccess } from "../../hooks/useAccess";

const STATUSES = ["open", "reviewing", "confirmed", "dismissed"];
const statusClass = {
  open: "badge bg-warning text-dark",
  reviewing: "badge bg-info text-dark",
  confirmed: "badge bg-danger",
  dismissed: "badge bg-secondary",
};

const userLabel = (user) => user?.username || user?.name || user?.uniqueId || "Unknown user";
const formatNumber = (value) => Number(value || 0).toLocaleString();
const formatDateTime = (value) => (value ? new Date(value).toLocaleString("en-PH", { timeZone: "Asia/Manila" }) : "—");
const formatScanDate = (value) =>
  value ? new Date(value).toLocaleDateString("en-PH", { timeZone: "Asia/Manila" }) : "—";

const SuspiciousGiftPairs = () => {
  const { canEdit, isSuperAdmin } = useAccess();
  const editable = isSuperAdmin || canEdit(FEATURES.BACKEND_SUSPICIOUS_GIFT_PAIRS);
  const [findings, setFindings] = useState([]);
  const [pagination, setPagination] = useState({ page: 1, limit: 20, total: 0 });
  const [filters, setFilters] = useState({ status: "", scanDate: "", search: "" });
  const [searchInput, setSearchInput] = useState("");
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState("");
  const [selected, setSelected] = useState(null);
  const [reviewStatus, setReviewStatus] = useState("open");
  const [reviewNotes, setReviewNotes] = useState("");
  const [saving, setSaving] = useState(false);

  const loadFindings = useCallback(async () => {
    setLoading(true);
    setError("");
    try {
      const params = { page: pagination.page, limit: pagination.limit };
      if (filters.status) params.status = filters.status;
      if (filters.scanDate) params.scanDate = filters.scanDate;
      if (filters.search) params.search = filters.search;
      const response = await axios.get("suspiciousGiftPairs", { params });
      setFindings(response.data.findings || []);
      setPagination((current) => ({ ...current, ...(response.data.pagination || {}) }));
    } catch (requestError) {
      setError(requestError.response?.data?.message || requestError.message || "Unable to load findings.");
    } finally {
      setLoading(false);
    }
  }, [filters, pagination.page, pagination.limit]);

  useEffect(() => {
    loadFindings();
  }, [loadFindings]);

  const openReview = (finding) => {
    setSelected(finding);
    setReviewStatus(finding.status);
    setReviewNotes(finding.reviewNotes || "");
  };

  const saveReview = async () => {
    if (!selected || !editable) return;
    setSaving(true);
    try {
      const response = await axios.patch(`suspiciousGiftPairs/${selected._id}/status`, {
        status: reviewStatus,
        reviewNotes,
      });
      const updated = response.data.finding;
      setFindings((current) => current.map((item) => (item._id === updated._id ? updated : item)));
      setSelected(null);
      Toast("success", "Review status updated.");
    } catch (requestError) {
      Toast("error", requestError.response?.data?.message || requestError.message);
    } finally {
      setSaving(false);
    }
  };

  const applySearch = (event) => {
    event.preventDefault();
    setPagination((current) => ({ ...current, page: 1 }));
    setFilters((current) => ({ ...current, search: searchInput.trim() }));
  };

  return (
    <>
      <div className="page-title">
        <div className="row">
          <div className="col-12 col-md-6 order-md-1 order-last">
            <h3 className="mb-2 text-white">Suspicious Gift Pairs</h3>
          </div>
          <div className="col-12 col-md-6 order-md-2 order-first">
            <nav aria-label="breadcrumb" className="breadcrumb-header float-start float-lg-end">
              <ol className="breadcrumb">
                <li className="breadcrumb-item"><Link to="/admin/dashboard" className="text-danger">Dashboard</Link></li>
                <li className="breadcrumb-item active" aria-current="page">Suspicious Gift Pairs</li>
              </ol>
            </nav>
          </div>
        </div>
      </div>

      <div className="card">
        <div className="card-header pb-0">
          <form className="row g-3 align-items-end my-2" onSubmit={applySearch}>
            <div className="col-12 col-lg-4">
              <label htmlFor="gift-pair-search" className="form-label">User search</label>
              <input id="gift-pair-search" className="form-control" value={searchInput}
                onChange={(event) => setSearchInput(event.target.value)} placeholder="Name, username, email, or unique ID" />
            </div>
            <div className="col-6 col-lg-3">
              <label htmlFor="gift-pair-status" className="form-label">Status</label>
              <select id="gift-pair-status" className="form-control" value={filters.status}
                onChange={(event) => { setPagination((p) => ({ ...p, page: 1 })); setFilters((f) => ({ ...f, status: event.target.value })); }}>
                <option value="">All statuses</option>
                {STATUSES.map((status) => <option key={status} value={status}>{status}</option>)}
              </select>
            </div>
            <div className="col-6 col-lg-3">
              <label htmlFor="gift-pair-date" className="form-label">Scan date</label>
              <input id="gift-pair-date" type="date" className="form-control" value={filters.scanDate}
                onChange={(event) => { setPagination((p) => ({ ...p, page: 1 })); setFilters((f) => ({ ...f, scanDate: event.target.value })); }} />
            </div>
            <div className="col-12 col-lg-2"><button className="btn btn-danger w-100" type="submit">Search</button></div>
          </form>
        </div>

        <div className="card-body card-overflow">
          {error && <div className="alert alert-danger" role="alert">{error} <button type="button" className="btn btn-sm btn-outline-light ms-2" onClick={loadFindings}>Retry</button></div>}
          <div className="table-responsive">
            <table className="table table-striped align-middle">
              <thead className="text-center"><tr>
                <th>Scan date</th><th>User A → B</th><th>User B → A</th><th>Combined</th>
                <th>Dominant direction</th><th>Share</th><th>Transactions</th><th>Time range</th><th>Status</th><th>Review</th>
              </tr></thead>
              <tbody className="text-center">
                {loading ? <tr><td colSpan="10" className="py-5"><span className="spinner-border spinner-border-sm text-danger me-2" role="status" aria-hidden="true"></span>Loading findings…</td></tr>
                  : findings.length === 0 ? <tr><td colSpan="10" className="py-5">No suspicious gift pairs found.</td></tr>
                    : findings.map((finding) => <tr key={finding._id}>
                      <td>{formatScanDate(finding.scanDate)}</td>
                      <td><strong>{userLabel(finding.userA)}</strong><br /><span className="text-muted">{formatNumber(finding.userAToUserBDiamonds)} diamonds</span></td>
                      <td><strong>{userLabel(finding.userB)}</strong><br /><span className="text-muted">{formatNumber(finding.userBToUserADiamonds)} diamonds</span></td>
                      <td>{formatNumber(finding.combinedDiamonds)}</td>
                      <td>{userLabel(finding.dominantSender)} → {userLabel(finding.dominantReceiver)}</td>
                      <td><strong>{(Number(finding.dominantShare) * 100).toFixed(1)}%</strong></td>
                      <td>{formatNumber(finding.transactionCount)}</td>
                      <td><small>{formatDateTime(finding.firstTransactionAt)}<br />to {formatDateTime(finding.lastTransactionAt)}</small></td>
                      <td><span className={statusClass[finding.status]}>{finding.status}</span></td>
                      <td><button type="button" className="btn btn-sm btn-outline-danger" onClick={() => openReview(finding)}>{editable ? "Review" : "View"}</button></td>
                    </tr>)}
              </tbody>
            </table>
          </div>
          <TablePagination component="div" count={pagination.total} page={pagination.page - 1}
            rowsPerPage={pagination.limit} rowsPerPageOptions={[10, 20, 50, 100]}
            onChangePage={(_event, page) => setPagination((current) => ({ ...current, page: page + 1 }))}
            onChangeRowsPerPage={(event) => setPagination((current) => ({ ...current, page: 1, limit: Number(event.target.value) }))}
            ActionsComponent={TablePaginationActions} />
        </div>
      </div>

      <Dialog open={Boolean(selected)} onClose={() => !saving && setSelected(null)} fullWidth maxWidth="sm" aria-labelledby="gift-review-title">

        <DialogContent className="text-white">
          {selected && <p><strong>{userLabel(selected.userA)}</strong> and <strong>{userLabel(selected.userB)}</strong> exchanged {formatNumber(selected.combinedDiamonds)} diamonds; the dominant direction was {(selected.dominantShare * 100).toFixed(1)}%.</p>}
          <label htmlFor="review-status" className="form-label">Status</label>
          <Select id="review-status" className="text-white" fullWidth value={reviewStatus} disabled={!editable || saving} onChange={(event) => setReviewStatus(event.target.value)}>
            {STATUSES.map((status) => <MenuItem className="text-white" key={status} value={status}>{status}</MenuItem>)}
          </Select>
          <label htmlFor="review-notes" className="form-label mt-4">Review notes (optional)</label>
          <textarea id="review-notes" className="form-control text-white" rows="5" maxLength="2000" value={reviewNotes}
            disabled={!editable || saving} onChange={(event) => setReviewNotes(event.target.value)} />
          {selected?.reviewedBy && <small className="text-white d-block mt-2">Last reviewed by {selected.reviewedBy.name || selected.reviewedBy.email} on {formatDateTime(selected.reviewedAt)}.</small>}
        </DialogContent>
        <DialogActions className="text-white">
          <button type="button" className="btn btn-outline-secondary" disabled={saving} onClick={() => setSelected(null)}>Close</button>
          {editable && <button type="button" className="btn btn-danger" disabled={saving} onClick={saveReview}>{saving ? "Saving…" : "Save review"}</button>}
        </DialogActions>
      </Dialog>
    </>
  );
};

export default SuspiciousGiftPairs;
